import React from 'react'
import { Button } from '../../components/ui-blocks/Button'
import { H1, H2, Paragraph, SmallParagraph, SmallText } from '../../components/typography/Typography'
import RotatingText from '../../components/typography/RotatingText'
import { WhatIsTelescopic } from './WhatIsTelescopic'
import { IndustriesSection } from './IndustriesSection'
import { ProductRange } from './ProductRange'
import { RestOfTheHall } from './RestOfTheHall'
import { SportsFlooring } from './SportsFlooring'
import { FacilityEquipment } from './FacilityEquipment'
import { Projects } from './Projects'
import { Certifications } from './Certifications'
import { PressCoverage } from './PressCoverage'
import { WhyXtractPro } from './WhyXtractPro'
import { useNavigate } from 'react-router-dom'
import heroImg2 from '../../assets/hero-image-2.webp'
import retractGif from '../../assets/retractable_seating_animation.gif'
import { FAQSection, generateFAQSchema } from '../../components/ui-blocks/FAQSection'
import { AnimatedCounter } from '../../components/ui-blocks/AnimatedCounter'
import { Helmet } from 'react-helmet-async'
import { ANALYTICS_EVENTS, track } from '../../lib/analytics'

export const Home = () => {
    const navigate = useNavigate()

    const homepageSchema = {
        "@context": "https://schema.org",
        "@graph": [
            organizationSchema,
            localBusinessSchema,
            generateFAQSchema(faqItems),
            websiteSchema
        ]
    }

    return (
        <>
            <Helmet>
                <title>XtractPro Seating | Telescopic Seating Systems in India</title>
                <meta name="description" content="XtractPro Seating is India's leading manufacturer of telescopic, retractable, and stadium seating systems for schools, auditoriums, stadiums, and arenas." />
                <script type="application/ld+json">
                    {JSON.stringify(homepageSchema)}
                </script>
            </Helmet>
            <div className='flex flex-col gap-8 md:gap-16 pb-8 md:pb-24'>
                <main className='flex flex-col lg:flex-row items-center py-8 md:py-0 bg-gray-100'>
                    <div className='flex flex-col px-4 md:px-24 md:py-16 gap-4 md:gap-12'>
                        <SmallText className='text-my-orange font-medium'>TELESCOPIC SEATING SOLUTIONS</SmallText>
                        <span>
                            <H1>Seating Solutions for</H1>
                            <RotatingText
                                texts={['Sports Stadiums', 'Schools', 'Convention Centers', 'Arenas', 'Theaters']}
                                staggerFrom={"center"}
                                initial={{ y: "100%" }}
                                animate={{ y: 0 }}
                                exit={{ y: "-120%" }}
                                staggerDuration={0.025}
                                splitLevelClassName="overflow-hidden pb-0.5 sm:pb-1 md:pb-1"
                                transition={{ type: "spring", damping: 30, stiffness: 400 }}
                                rotationInterval={2000}
                                mainClassName='justify-start'
                                elementLevelClassName='font-inter text-4xl md:text-6xl font-light tracking-tight text-gray-900'
                            />
                        </span>
                        <div className='flex flex-row gap-4 md:gap-8 mt-4 md:mt-8'>
                            <Button variant='primary' onClick={() => {
                                track(ANALYTICS_EVENTS.HOME_HERO_CTA_CLICKED, { cta: 'enquire_now', destination: '/contact' })
                                navigate('/contact')
                            }}>Talk To Us&nbsp;&rarr;</Button>
                            <Button variant='secondary' onClick={() => {
                                track(ANALYTICS_EVENTS.HOME_HERO_CTA_CLICKED, { cta: 'explore', destination: '/stadium-seating' })
                                navigate('/stadium-seating')
                            }}>Explore Seating</Button>
                        </div>
                    </div>
                    <img
                        src='/hero-image.webp'
                        alt='retractable seating'
                        width={721}
                        height={511}
                        fetchPriority='high'
                        decoding='async'
                        className='w-full md:w-[50%] h-auto aspect-[721/511]'
                    />
                </main>



                {/* One wrapper so the page's section gap doesn't open a band of
                    page background between the product range and the counter. */}
                <div className='bg-white'>
                    <ProductRange />

                    <div className='flex flex-col items-center gap-2 md:gap-4 border-t border-gray-300 mx-4 md:mx-24 pt-8 md:pt-12 pb-8 md:pb-12'>
                        <AnimatedCounter
                            to={85153}
                            suffix='+'
                            className='text-5xl md:text-7xl font-light tracking-tight text-gray-900'
                        />
                        <SmallParagraph className='uppercase tracking-widest text-gray-600'>
                            Seats Installed
                        </SmallParagraph>
                    </div>
                </div>


                <WhatIsTelescopic />
                <IndustriesSection />
                <RestOfTheHall />
                <SportsFlooring />

                <FacilityEquipment />

                <Projects />

                <Certifications />

                <WhyXtractPro />


                <div className='flex flex-col lg:flex-row gap-4'>
                    <div className='flex flex-col w-full lg:w-1/2 px-4 md:px-24 gap-4'>
                        <H2>Who We Are</H2>

                        <Paragraph>
                            XtractPro Seating is a leading manufacturer, trader, and exporter of telescopic seating systems based in India. As one of the few companies that design and manufacture their seating solutions entirely in-house, we bring proven expertise from large-scale government projects to schools, auditoriums, and community venues.
                        </Paragraph>

                        <div className='flex flex-col md:flex-row gap-2 md:justify-between'>
                            <SmallParagraph className='md:w-1/2 text-gray-600'>
                                We specialize in telescopic seating, stadium seating, and auditorium seating systems designed for durability, safety, and efficient use of space. From concept and engineering to manufacturing and installation, every project is delivered with a focus on performance and long-term reliability.
                            </SmallParagraph>

                            <SmallParagraph className='md:w-1/2 text-gray-600'>
                                Our team works closely with architects, contractors, and institutions to develop seating solutions tailored to real-world needs—whether optimizing space in a school auditorium or delivering high-capacity seating systems for stadiums and arenas.
                            </SmallParagraph>
                        </div>
                    </div>
                    <img src={retractGif} alt='retractable seating' className='lg:w-[50%] px-4 md:px-16 aspect-[423/294]' />
                </div>
                <div className='flex flex-col-reverse lg:flex-row gap-4'>
                    <img
                        src={heroImg2}
                        alt='retractable seating'
                        width={665}
                        height={501}
                        loading='lazy'
                        decoding='async'
                        className='lg:w-[50%] h-auto aspect-[665/501] px-4 md:px-16'
                    />
                    <div className='flex flex-col w-full lg:w-1/2 px-4 md:px-24 gap-4'>
                        <H2>Our Mission</H2>
                        <Paragraph>is to revolutionize the way audiences experience events by providing innovative seating solutions that maximize space and comfort. We are dedicated to enhancing the overall event experience through our cutting-edge designs and technology.</Paragraph>
                        <div className='flex flex-col md:flex-row gap-2 md:justify-between'>
                            <SmallParagraph className='md:w-1/2 text-gray-600'>With a relentless commitment to excellence, integrity, and collaboration, we strive to deliver superior products and services that exceed expectations and drive value for our stakeholders.</SmallParagraph>
                            <SmallParagraph className='md:w-1/2 text-gray-600'>We proudly support the Make in India initiative, with all of our manufacturing taking place domestically. We are one of the few manufacturers in the country to be able to achieve this.</SmallParagraph>
                        </div>
                        <div className='w-1/2 mt-4 mx-auto md:mx-0'>
                            <Button variant='primary' onClick={() => {
                                track(ANALYTICS_EVENTS.HOME_MISSION_CTA_CLICKED, { destination: '/stadium-seating' })
                                navigate('/stadium-seating')
                            }}>Learn More&nbsp;&rarr;</Button>
                        </div>
                    </div>
                </div>

                <FAQSection items={faqItems} />
                <PressCoverage />

            </div>
        </>
    )
}

const faqItems = [
    {
        question: "What is telescopic seating?",
        answer:
            "Telescopic seating is a motorised seating system that allows rows of seats to be extended for events and folded back when the space is needed for other activities. This type of seating is ideal for venues that require flexible use of space, such as school auditoriums, convention centers, and stadiums."
    },
    {
        question: "What is the difference between telescopic seating and retractable seating?",
        answer:
            "Telescopic seating and Retractable seating refer to the same type of seating system. Both terms are used interchangeably to describe seating that can be extended and retracted as needed."
    },
    {
        question: "Where is retractable stadium seating typically used?",
        answer:
            "Retractable stadium seating is commonly used in sports stadiums, arenas, universities, schools, concert halls, and large auditoriums."
    },
    {
        question: "Do you provide custom seating layouts?",
        answer:
            "Yes. Our seating systems are designed based on the venue layout, capacity requirements, and architectural constraints of each project."
    },
    {
        question: "What materials are used in your seating systems?",
        answer:
            "Our seating systems typically use galvanized steel structures, high-density polyethylene seats, and durable finishing materials designed for long-term use."
    }
]

const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "XtractPro Seating",
    "url": "https://xtractpro.in",
    "logo": "https://xtractpro.in/apple-touch-icon.png",
    "description": "Telescopic Seating Manufacturer for Stadiums, Schools, Colleges, and Theaters.",
    "sameAs": [
        "https://www.linkedin.com/company/xtractpro-seating",
        "https://www.instagram.com/xtractproseating?igsh=MWdiYWtwMjI1ZDQ2eg==",
        "https://youtube.com/@xtractproseating?si=aZxB-bfGjww6HRW6",
        "https://www.facebook.com/share/1JppMFrhVC/?mibextid=wwXIfr"
    ],
    "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-9266683150",
        "contactType": "sales",
        "areaServed": "IN"
    }
}

const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "XtractPro Seating",
    "image": "https://xtractpro.in/apple-touch-icon.png",
    "url": "https://xtractpro.in",
    "telephone": "+91-9266683150",
    "address": {
        "@type": "PostalAddress",
        "addressLocality": "Haryana",
        "addressCountry": "IN",
        "streetAddress": "9-F, Moulsari Avenue, DLF Phase 3, Sector 24,",
        "postalCode": "122002"
    },
    "areaServed": "India"
}

const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "XtractPro Seating",
    "url": "https://xtractpro.in"
}